import { useEffect, useRef, type ReactNode } from 'react';
import { useI18n } from '@/i18n/I18nProvider';
import { cn } from '@/lib/macondo';

/**
 * The page's one <h1>. On mount it takes focus so a keyboard or screen reader user lands at the
 * top of the new page after a route change (WCAG 2.4.3), and it keeps document.title in step.
 * Screens with a form (login, code) pass `focus={false}` and focus their field instead.
 */
export function PageHeading({
  children,
  title,
  focus = true,
  className,
}: {
  children: ReactNode;
  /** Plain-text version for document.title. Defaults to children when it is a string. */
  title?: string;
  focus?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  const { t } = useI18n();
  const text = title ?? (typeof children === 'string' ? children : '');

  useEffect(() => {
    if (focus) ref.current?.focus({ preventScroll: true });
    // Only on mount: a re-render of the same page must not steal focus again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = text ? `${text} | ${t('app.title')}` : t('app.title');
  }, [text, t]);

  return (
    <h1 ref={ref} tabIndex={-1} className={cn('text-h2 text-typography-primary outline-none', className)}>
      {children}
    </h1>
  );
}
